import { useEffect, useState } from 'react';
import ActionButton from '../components/ActionButton';
import FormContainer from '../components/FormContainer';
import InputCheckbox from '../components/InputCheckbox';
import InputText from '../components/InputText';
import Logo from '../components/Logo';
import PhoneInput from '../components/PhoneInput';
import { useTheme } from '../hooks/useTheme';
import {
  ValidationError,
  validEmail,
  validName,
  validPhoneNumber,
} from '../utils/Validations';
import { useToast } from '../hooks/useToast';
import useMktWeb from '../hooks/useMktWeb';
import { CountryData } from 'react-phone-input-2';
import { gtmEventSubmitForm } from '../utils/gtm';
import usePageContext, { PageContext } from '../hooks/usePageContext';

export default function ContactFormPage() {
  const { theme } = useTheme();
  const { context } = usePageContext();
  const isIaPage =
    context === PageContext.IA_BUILDER ||
    context === PageContext.MBA_IA_BUSINESS;
  const { errorToast } = useToast();
  const { sendWhatsappLead } = useMktWeb();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('br');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPos, setIsPos] = useState(false);
  const [isPlus, setIsPlus] = useState(false);
  const [redirectLink, setRedirectLink] = useState('');
  const [showModal, setShowModal] = useState(false);

  const validate = async () => {
    try {
      setLoading(true);
      if (!name) throw new ValidationError('Nome é obrigatório');
      if (!email) throw new ValidationError('Nome é obrigatório');
      if (!phone) throw new ValidationError('Número de Whatsapp é obrigatório');
      if (!agreed)
        throw new ValidationError(
          'É necessário concordar com a política de privacidade'
        );
      validName(name);
      validPhoneNumber(phone, country);
      validEmail(email);
      await send();
      clear();
    } catch (e) {
      console.log(e);
      if (e instanceof ValidationError) {
        errorToast(e.message);
      } else {
        errorToast(
          'Não foi possível realizar a operação. Tente novamente mais tarde.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setName('');
    setEmail('');
    setPhone('');
    setAgreed(false);
  };

  useEffect(() => {
    const host = window.location.host;
    const urlIncludesPos = host.includes('pos');
    setIsPos(urlIncludesPos);
  }, []);

  useEffect(() => {
    const host = window.location.host;
    const urlIncludesPlus = host.includes('plus');
    setIsPlus(urlIncludesPlus);
  }, []);

  const send = async () => {
    const phoneRedirect = await sendWhatsappLead({
      name: name,
      email: email,
      lgpd: agreed,
      phone: phone
    }, theme.phone);
    if (isPos || isPlus) {
      gtmEventSubmitForm(email, phone);
    }
    const link = `https://wa.me/${phoneRedirect}`;
    setRedirectLink(link);
    window.open(link, '_blank');
    setShowModal(true);
  };

  const changePhone = (phone: string, country: CountryData) => {
    setPhone(phone);
    setCountry(country.countryCode);
  };

  const isFormValid = !!name && !!email && !!phone && agreed;

  return (
    <div
      className="w-full h-screen flex justify-content-center background"
      style={{ backgroundImage: theme.background.path }}
    >
      <div className="p-4" style={{ maxWidth: 450 }}>
        <center className="pb-6 pt-4">
          <Logo />
        </center>
        <FormContainer>
          <div className="text-center px-2 mb-3 text-lg">
            <strong>Fale com o nosso time por WhatsApp</strong>
          </div>
          <div className="text-center px-2 mb-3">
            Insira seus dados de contato e o direcionaremos para o melhor
            atendimento.
          </div>
          <div>
            <div className="field">
              <label>Nome completo</label>
              <InputText
                className="w-full"
                placeholder={theme.placeholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label>E-mail</label>
              <InputText
                type="text"
                className="w-full"
                placeholder="Ex: seuemail@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Celular (WhatsApp)</label>
              <PhoneInput value={phone} onChange={changePhone} />
            </div>
            <div className="field-checkbox mt-4 text-white-alpha-60">
              <InputCheckbox
                className="mr-2"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="text-xs">
                Concordo em receber comunicações da Conquer de acordo com a{' '}
                <a
                  href="https://escolaconquer.com.br/POL%C3%8DTICA_DE_PRIVACIDADE_E_COOKIES-CONQUER.pdf"
                  target="noopener"
                  style={{ color: theme.privacyPolicy }}
                >
                  Política de Privacidade
                </a>
                .
              </label>
            </div>
            <ActionButton
              className="w-full mt-4"
              onClick={validate}
              loading={loading}
              disabled={!isFormValid}
            >
              <div className="flex align-content-center justify-content-center">
                <img
                  src="/img/whatsapp.svg"
                  width={17}
                  style={{ marginRight: 10 }}
                  alt=""
                />
                <div>Entrar em contato</div>
              </div>
            </ActionButton>
          </div>
        </FormContainer>
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div
            className="modal-wrapper"
            style={{ border: `2px solid ${theme.color.primary}` }}>
            <button
              className="close-button"
              onClick={() => setShowModal(false)}
              style={{ border: `2px solid ${theme.color.primary}` }}>
              X
            </button>
            <div className="container" id={isIaPage ? 'form-success' : undefined}>
              <div className="title">
                <h3>
                  Uma nova <strong>aba do WhatsApp</strong> será aberta em seu navegador.
                </h3>
              </div>
              <p className="subtitle">
                Se você não foi direcionado(a),{' '}
                <a href={redirectLink} target="_blank" rel="noopener noreferrer">
                  clique aqui para tentar novamente.
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
